import React, { useState, useMemo, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Timer,
  Clock,
  Cpu,
  Volume2,
  TrendingDown,
  TrendingUp,
  Sparkles,
  Filter,
  CheckCircle,
  AlertTriangle,
  Info,
  Layers,
  X,
} from "lucide-react";
import LatencyService from "../../services/Latency.service";
import Pagination from "../../components/Pagination/Pagination";
import Datepicker from "../../components/Datepicker/Datepicker";
import Dropdown from "../../components/Dropdown/Dropdown";
import ApiService from "../../services/Api.service";
import moment from "moment";
import Loader from "../../components/Loader/Loader";
import toast from "react-hot-toast";
import "./LatencyInsights.css";

const CALL_DURATION_OPTIONS = [
  { value: 0, label: "All Calls (0s)" },
  { value: 30, label: ">= 30 sec" },
  { value: 60, label: ">= 60 sec (Default)" },
  { value: 90, label: ">= 90 sec" },
  { value: 120, label: ">= 120 sec (2 min)" },
  { value: 180, label: ">= 180 sec (3 min)" },
  { value: 300, label: ">= 300 sec (5 min)" },
];

const MIN_TURNS_OPTIONS = [
  { value: 0, label: "All Turns (0)" },
  { value: 1, label: ">= 1 Turn" },
  { value: 2, label: ">= 2 Turns" },
  { value: 3, label: ">= 3 Turns (Default)" },
  { value: 5, label: ">= 5 Turns" },
  { value: 10, label: ">= 10 Turns" },
];

export default function LatencyInsights() {
  const [duration, setDuration] = useState("1_week");
  const [callDuration, setCallDuration] = useState(60);
  const [minTurns, setMinTurns] = useState(3);
  const [expandedCallId, setExpandedCallId] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState("All");

  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(10);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [dateError, setDateError] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);

  const handleStartDateChange = (date) => {
    setTempStartDate(date);
    if (tempEndDate && date > tempEndDate) {
      setDateError("Start date cannot be after end date");
    } else {
      setDateError("");
    }
  };

  const handleEndDateChange = (date) => {
    setTempEndDate(date);
    if (tempStartDate && date < tempStartDate) {
      setDateError("End date cannot be before start date");
    } else {
      setDateError("");
    }
  };

const handleApplyFilters = () => {
  if (dateError) return;

  // If neither date is selected, clear custom range and revert to default duration
  if (!tempStartDate && !tempEndDate) {
    setStartDate(null);
    setEndDate(null);
    setShowFilterModal(false);
    setDuration("1_week");
    return;
  }

  // If only one date is selected, block apply
  if (!tempStartDate || !tempEndDate) {
    setDateError("Please select both start and end dates");
    return;
  }

  setStartDate(tempStartDate);
  setEndDate(tempEndDate);
  setShowFilterModal(false);
  setDuration(null);
};

  const handleClearFilters = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    setStartDate(null);
    setEndDate(null);
    setDateError("");
    setDuration("1_week");
    setCallDuration(60);
    setMinTurns(3);
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await ApiService.getCompanyOptions();
        if (res.status === 200 && res.data) {
          const list = res.data.data || [];
          const fullList = [{ value: "All", label: "All Companies" }, ...list];
          setCompanyOptions(fullList);
          setSelectedCompany("All");
        } else {
          toast.error("Failed to fetch company list");
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
        toast.error("Error loading company list");
      }
    };
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (!selectedCompany) {
      setCampaignsList([{ value: "All", label: "All Campaigns" }]);
      return;
    }

    if (selectedCompany === "All") {
      setCampaignsList([{ value: "All", label: "All Campaigns" }]);
      setSelectedCampaign("All");
      return;
    }

    const fetchCampaigns = async () => {
      try {
        const res = await ApiService.getCampaignOptions({
          params: { company_id: selectedCompany }
        });
        if (res.status === 200 && res.data) {
          const list = res.data.data || [];
          setCampaignsList([{ value: "All", label: "All Campaigns" }, ...list]);
        } else {
          toast.error("Failed to fetch campaigns list");
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        toast.error("Error loading campaigns list");
      }
    };

    fetchCampaigns();
  }, [selectedCompany]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [campaignsList, setCampaignsList] = useState([{ value: "All", label: "All Campaigns" }]);

  useEffect(() => {
    if (companyOptions.length > 0 && !selectedCompany) {
      return;
    }

    let isMounted = true;
    const fetchInsights = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { 
          campaign_name: selectedCampaign,
          call_duration: callDuration,
          min_turns: minTurns
        };

        if (startDate && endDate) {
          params.start_date = moment(startDate).format("YYYY-MM-DD");
          params.end_date = moment(endDate).format("YYYY-MM-DD");
        } else if (duration) {
          params.duration = duration;
        }

        if (selectedCompany) {
          params.company_id = selectedCompany;
        }

        const response = await LatencyService.getLatencyInsights(params);
        if (isMounted) {
          const data = response.data;
          setDashboardData(data);


        }
      } catch (err) {
        console.error("Error fetching latency insights:", err);
        if (isMounted) {
          setError(err.response?.data?.detail || err.message || "Failed to load latency data.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchInsights();
    return () => {
      isMounted = false;
    };
  }, [duration, selectedCampaign, selectedCompany, startDate, endDate, companyOptions, callDuration, minTurns]);

  const currentData = useMemo(() => {
    if (dashboardData) return dashboardData;
    return {
      periodName: "Loading insights...",
      kpis: {
        overall: { value: 0, delta: "0.0%", isPositive: true },
        stt: { value: 0, delta: "0.0%", isPositive: true },
        llm: { value: 0, delta: "0.0%", isPositive: true },
        tts: { value: 0, delta: "0.0%", isPositive: true },
      },
      trends: [],
      vendors: { stt: [], llm: [], tts: [] },
      calls: [],
    };
  }, [dashboardData]);

  const getSequentialDetails = (call) => {
    if (call.details) return call.details;
    const transcription = call.transcription || [];
    const sttList = call.stt_latency || [];
    const llmList = call.llm_latency_list || [];
    const ttsList = call.tts_latency_list || [];

    let sttIdx = 0;
    let llmIdx = 0;
    let ttsIdx = 0;

    return transcription.map((turn, index) => {
      const originalRole = turn.role || "Unknown";
      const text = turn.text || "";
      let sttVal = 0;
      let llmVal = 0;
      let ttsVal = 0;

      const lowerRole = originalRole.toLowerCase().trim();
      const isCustomer = lowerRole === "customer" || lowerRole === "user";
      const isAI = lowerRole === "ai" || lowerRole === "agent" || lowerRole === "assistant";

      let normalizedRole = "AI";
      if (isCustomer) {
        normalizedRole = "Customer";
        if (sttIdx < sttList.length) {
          sttVal = sttList[sttIdx];
          sttIdx++;
        }
      } else if (isAI) {
        normalizedRole = "AI";
        const isGreeting = (index === 0);
        if (!isGreeting) {
          if (llmIdx < llmList.length) {
            llmVal = llmList[llmIdx];
            llmIdx++;
          }
          if (ttsIdx < ttsList.length) {
            ttsVal = ttsList[ttsIdx];
            ttsIdx++;
          }
        }
      } else {
        normalizedRole = originalRole;
      }

      return {
        role: normalizedRole,
        text,
        stt: typeof sttVal === "number" ? Math.round(sttVal * 100) / 100 : 0,
        llm: typeof llmVal === "number" ? Math.round(llmVal * 100) / 100 : 0,
        tts: typeof ttsVal === "number" ? Math.round(ttsVal * 100) / 100 : 0,
      };
    });
  };

  const filteredCalls = useMemo(() => {
    const rawCalls = currentData.calls;
    let list = [];
    if (rawCalls && typeof rawCalls === "object" && !Array.isArray(rawCalls) && rawCalls.data) {
      list = rawCalls.data;
    } else if (Array.isArray(rawCalls)) {
      list = rawCalls;
    }
    if (minTurns > 0) {
      return list.filter((c) => (c.transcription ? c.transcription.length >= minTurns : true));
    }
    return list;
  }, [currentData.calls, minTurns]);

  const callsTableData = useMemo(() => {
    const rawCalls = currentData.calls;
    const startIndex = (currentPage - 1) * postsPerPage;
    const paginated = filteredCalls.slice(startIndex, startIndex + postsPerPage);
    const currentHeaders = rawCalls?.header || [];

    return {
      data: paginated,
      header: currentHeaders,
      pagination: {
        page: currentPage,
        size: postsPerPage,
        total: filteredCalls.length,
        total_pages: Math.ceil(filteredCalls.length / postsPerPage) || 1
      }
    };
  }, [currentData.calls, filteredCalls, currentPage, postsPerPage]);

  const paginatedCalls = callsTableData.data;
  const paginationObj = callsTableData.pagination;
  const headers = callsTableData.header;

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedCallId(null);
  };

  const handlePageSizeChange = (e) => {
    setPostsPerPage(Number(e.target.value));
    setCurrentPage(1);
    setExpandedCallId(null);
  };

  useEffect(() => {
    setSelectedCampaign("All");
    setCampaignsList([{ value: "All", label: "All Campaigns" }]);
  }, [selectedCompany]);

  useEffect(() => {
    setCurrentPage(1);
    setExpandedCallId(null);
  }, [duration, startDate, endDate, selectedCompany, selectedCampaign, callDuration, minTurns]);

  const toggleExpandRow = (callId) => {
    setExpandedCallId(prev => (prev === callId ? null : callId));
  };

  return (
    <div className="latency-container">
      <div className="latency-header-panel">
        <p className="latency-subtitle-main">
          Real-time performance profiles for Speech-to-Text, LLM Processing, and Text-to-Speech synthesis engines
        </p>
      </div>

      <div className="latency-controls-row">
        <div className="latency-controls-left">
          <div className="latency-company-select-wrapper">
            <Dropdown
              name="company_ids"
              value={selectedCompany}
              onChange={(e) => {
                setSelectedCompany(e.target.value);
                setExpandedCallId(null);
              }}
              options={companyOptions}
              placeholder="Company"
              showLegend={true}
            />
          </div>

          <div className="latency-company-select-wrapper">
            <Dropdown
              name="campaign_name"
              value={selectedCampaign}
              onChange={(e) => {
                setSelectedCampaign(e.target.value);
                setExpandedCallId(null);
                setCurrentPage(1);
              }}
              options={campaignsList}
              placeholder="Campaign"
              showLegend={true}
            />
          </div>

          <div className="latency-company-select-wrapper">
            <Dropdown
              name="call_duration"
              value={callDuration}
              onChange={(e) => {
                setCallDuration(Number(e.target.value));
                setExpandedCallId(null);
                setCurrentPage(1);
              }}
              options={CALL_DURATION_OPTIONS}
              placeholder="Call Duration"
              showLegend={true}
            />
          </div>

          <div className="latency-company-select-wrapper">
            <Dropdown
              name="min_turns"
              value={minTurns}
              onChange={(e) => {
                setMinTurns(Number(e.target.value));
                setExpandedCallId(null);
                setCurrentPage(1);
              }}
              options={MIN_TURNS_OPTIONS}
              placeholder="Min Turns"
              showLegend={true}
            />
          </div>

          <div className="latency-inline-filter-wrapper">
            <button
              className={`latency-filter-btn ${(startDate || endDate) ? "active" : ""}`}
              onClick={() => setShowFilterModal(!showFilterModal)}
            >
              <Filter size={16} />
              <span>{(startDate || endDate) ? "Custom Range" : "Custom Dates"}</span>
            </button>

            {showFilterModal && (
              <div className="latency-inline-filter-popup">
                <div className="latency-inline-filter-header">
                  <h4>Filter Latency Analytics</h4>
                  <button
                    className="latency-inline-close-btn"
                    onClick={() => setShowFilterModal(false)}
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="latency-inline-filter-body">
                  <div className="latency-inline-filter-group">
                    <label>Start Date</label>
                    <Datepicker
                      name="startDate"
                      value={tempStartDate}
                      onChange={handleStartDateChange}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="Select Start Date"
                      errorMessage={dateError}
                      errorClass={!!dateError}
                    />
                  </div>

                  <div className="latency-inline-filter-group">
                    <label>End Date</label>
                    <Datepicker
                      name="endDate"
                      value={tempEndDate}
                      onChange={handleEndDateChange}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="Select End Date"
                      errorMessage={dateError}
                      errorClass={!!dateError}
                    />
                  </div>

                  {dateError && <p className="latency-inline-filter-error">{dateError}</p>}

                  <div className="latency-inline-filter-footer">
                    <button className="latency-inline-clear-btn" onClick={handleClearFilters}>
                      Clear
                    </button>
                    <button className="latency-inline-apply-btn" onClick={handleApplyFilters}>
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="latency-period-badge">
          <Sparkles className="latency-badge-glow" /> Active View: {currentData.periodName}
        </div>
      </div>

      {loading && !dashboardData ? (
        <Loader />
      ) : error && !dashboardData ? (
<div className="latency-error-container">
  <AlertTriangle className="latency-error-icon" />
  <h4 className="latency-error-title">Database Error</h4>
  <p className="latency-error-desc">{error}</p>
  <button
    className="latency-btn-retry"
    onClick={async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { campaign_name: selectedCampaign };

        if (startDate && endDate) {
          params.start_date = moment(startDate).format("YYYY-MM-DD");
          params.end_date = moment(endDate).format("YYYY-MM-DD");
        } else if (duration) {
          params.duration = duration;
        }

        if (selectedCompany) {
          params.company_id = selectedCompany;
        }

        const response = await LatencyService.getLatencyInsights(params);
        setDashboardData(response.data);
      } catch (err) {
        console.error("Error fetching latency insights:", err);
        setError(
          err.response?.data?.detail || err.message || "Failed to load latency data."
        );
      } finally {
        setLoading(false);
      }
    }}
  >
    Retry Fetch
  </button>
</div>
      ) : (
        <>
          <div className="latency-kpi-grid">
            <div className="latency-kpi-card premium-border-purple">
              <div className="latency-kpi-header">
                <span className="latency-kpi-lbl">Overall Latency</span>
                <div className="latency-kpi-icon-wrap bg-purple-g">
                  <Timer className="latency-icon-kpi-p" />
                </div>
              </div>
              <div className="latency-kpi-body">
                <h2 className="latency-kpi-val">{currentData.kpis.overall.value}s</h2>
                <div className={`latency-kpi-trend ${currentData.kpis.overall.isPositive ? "pos" : "neg"}`}>
                  {currentData.kpis.overall.isPositive ? (
                    <TrendingDown className="trend-icon" />
                  ) : (
                    <TrendingUp className="trend-icon" />
                  )}
                  {" "}{currentData.kpis.overall.delta}
                  <span className="trend-text"> vs prev period</span>
                </div>
              </div>
              <div className="card-glass-glow-purple" />
            </div>

            <div className="latency-kpi-card premium-border-blue">
              <div className="latency-kpi-header">
                <span className="latency-kpi-lbl">Speech-to-Text (STT)</span>
                <div className="latency-kpi-icon-wrap bg-blue-g">
                  <Clock className="latency-icon-kpi-b" />
                </div>
              </div>
              <div className="latency-kpi-body">
                <h2 className="latency-kpi-val">{currentData.kpis.stt.value}s</h2>
                <div className={`latency-kpi-trend ${currentData.kpis.stt.isPositive ? "pos" : "neg"}`}>
                  {currentData.kpis.stt.isPositive ? (
                    <TrendingDown className="trend-icon" />
                  ) : (
                    <TrendingUp className="trend-icon" />
                  )}
                  {" "}{currentData.kpis.stt.delta}
                  <span className="trend-text"> vs prev period</span>
                </div>
              </div>
              <div className="card-glass-glow-blue" />
            </div>

            <div className="latency-kpi-card premium-border-green">
              <div className="latency-kpi-header">
                <span className="latency-kpi-lbl">LLM Reasoning</span>
                <div className="latency-kpi-icon-wrap bg-green-g">
                  <Cpu className="latency-icon-kpi-g" />
                </div>
              </div>
              <div className="latency-kpi-body">
                <h2 className="latency-kpi-val">{currentData.kpis.llm.value}s</h2>
                <div className={`latency-kpi-trend ${currentData.kpis.llm.isPositive ? "pos" : "neg"}`}>
                  {currentData.kpis.llm.isPositive ? (
                    <TrendingDown className="trend-icon" />
                  ) : (
                    <TrendingUp className="trend-icon" />
                  )}
                  {" "}{currentData.kpis.llm.delta}
                  <span className="trend-text"> vs prev period</span>
                </div>
              </div>
              <div className="card-glass-glow-green" />
            </div>

            <div className="latency-kpi-card premium-border-orange">
              <div className="latency-kpi-header">
                <span className="latency-kpi-lbl">Audio Synthesis (TTS)</span>
                <div className="latency-kpi-icon-wrap bg-orange-g">
                  <Volume2 className="latency-icon-kpi-o" />
                </div>
              </div>
              <div className="latency-kpi-body">
                <h2 className="latency-kpi-val">{currentData.kpis.tts.value}s</h2>
                <div className={`latency-kpi-trend ${currentData.kpis.tts.isPositive ? "pos" : "neg"}`}>
                  {currentData.kpis.tts.isPositive ? (
                    <TrendingDown className="trend-icon" />
                  ) : (
                    <TrendingUp className="trend-icon" />
                  )}
                  {" "}{currentData.kpis.tts.delta}
                  <span className="trend-text"> vs prev period</span>
                </div>
              </div>
              <div className="card-glass-glow-orange" />
            </div>
          </div>

          <div className="latency-charts-grid">
            <div className="latency-chart-card">
              <div className="latency-card-header">
                <div>
                  <h3 className="latency-card-title">Latency Trends over Time</h3>
                  <p className="latency-card-desc">Daily average latency profile for voice cycle modules</p>
                </div>
                <div className="latency-legend-indicators">
                  <span className="legend-indicator stt">STT</span>
                  <span className="legend-indicator llm">LLM</span>
                  <span className="legend-indicator tts">TTS</span>
                  <span className="legend-indicator overall">Overall</span>
                </div>
              </div>
              <div className="latency-chart-wrapper">
                {currentData.trends && currentData.trends.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <AreaChart data={currentData.trends} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="sttGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#a78bfa" stopOpacity={0.0} />
                        </linearGradient>
                        <linearGradient id="llmGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#60a5fa" stopOpacity={0.0} />
                        </linearGradient>
                        <linearGradient id="ttsGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f87171" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#f87171" stopOpacity={0.0} />
                        </linearGradient>
                        <linearGradient id="overallGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2dd4bf" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#2dd4bf" stopOpacity={0.0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} unit="s" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "rgba(255, 255, 255, 0.96)",
                          border: "1px solid #e2e8f0",
                          borderRadius: "10px",
                          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.05)",
                          fontSize: "12px",
                          color: "#0f172a",
                        }}
                      />
                      <Area type="monotone" dataKey="STT" stroke="#a78bfa" strokeWidth={2} fillOpacity={1} fill="url(#sttGrad)" />
                      <Area type="monotone" dataKey="LLM" stroke="#60a5fa" strokeWidth={2} fillOpacity={1} fill="url(#llmGrad)" />
                      <Area type="monotone" dataKey="TTS" stroke="#f87171" strokeWidth={2} fillOpacity={1} fill="url(#ttsGrad)" />
                      <Area type="monotone" dataKey="Overall" stroke="#2dd4bf" strokeWidth={2.5} fillOpacity={1} fill="url(#overallGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="latency-table-empty">
                    <Info className="empty-icon" />
                    <h4>No trend data available for this range</h4>
                  </div>
                )}
              </div>
            </div>

            <div className="latency-chart-card">
              <h3 className="latency-card-title">Engine Speed Benchmarks</h3>
              <p className="latency-card-desc">Average latency breakdown by third-party engines and APIs (seconds)</p>

              <div className="latency-benchmarks-list">
                <div className="benchmark-group">
                  <h4 className="benchmark-group-title">
                    <Cpu className="group-title-icon text-purple" /> Speech-to-Text Latency
                  </h4>
                  {currentData.vendors?.stt && currentData.vendors.stt.length > 0 ? (
                    currentData.vendors.stt.map((v, i) => (
                      <div key={i} className="benchmark-bar-row">
                        <div className="benchmark-bar-lbl">
                          <span>{v.name}</span>
                          <span className="calls-badge">{v.calls} calls</span>
                        </div>
                        <div className="benchmark-bar-outer">
                          <div
                            className="benchmark-bar-inner"
                            style={{
                              width: `${Math.min((v.latency / 1.3) * 100, 100)}%`,
                              backgroundColor: v.color || "#a78bfa",
                            }}
                          />
                          <span className="benchmark-val">{v.latency}s</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="latency-card-desc" style={{ fontStyle: "italic" }}>No STT engines used in this range.</p>
                  )}
                </div>

                <div className="benchmark-group">
                  <h4 className="benchmark-group-title">
                    <Layers className="group-title-icon text-blue" /> LLM Generation Latency
                  </h4>
                  {currentData.vendors?.llm && currentData.vendors.llm.length > 0 ? (
                    currentData.vendors.llm.map((v, i) => (
                      <div key={i} className="benchmark-bar-row">
                        <div className="benchmark-bar-lbl">
                          <span>{v.name}</span>
                          <span className="calls-badge">{v.calls} calls</span>
                        </div>
                        <div className="benchmark-bar-outer">
                          <div
                            className="benchmark-bar-inner"
                            style={{
                              width: `${Math.min((v.latency / 1.3) * 100, 100)}%`,
                              backgroundColor: v.color || "#60a5fa",
                            }}
                          />
                          <span className="benchmark-val">{v.latency}s</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="latency-card-desc" style={{ fontStyle: "italic" }}>No LLM models used in this range.</p>
                  )}
                </div>

                <div className="benchmark-group">
                  <h4 className="benchmark-group-title">
                    <Volume2 className="group-title-icon text-red" /> Audio Synthesis Latency
                  </h4>
                  {currentData.vendors?.tts && currentData.vendors.tts.length > 0 ? (
                    currentData.vendors.tts.map((v, i) => (
                      <div key={i} className="benchmark-bar-row">
                        <div className="benchmark-bar-lbl">
                          <span>{v.name}</span>
                          <span className="calls-badge">{v.calls} calls</span>
                        </div>
                        <div className="benchmark-bar-outer">
                          <div
                            className="benchmark-bar-inner"
                            style={{
                              width: `${Math.min((v.latency / 1.3) * 100, 100)}%`,
                              backgroundColor: v.color || "#f87171",
                            }}
                          />
                          <span className="benchmark-val">{v.latency}s</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="latency-card-desc" style={{ fontStyle: "italic" }}>No TTS synthesis engines used in this range.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="latency-card table-section">
            <div className="table-header">
              <div className="header-left">
                <h3 className="header-title">
                  Recent Call Latency Breakdown
                  {paginationObj.total > 0 && (
                    <span className="total-count">{paginationObj.total} Total</span>
                  )}
                </h3>
              </div>
            </div>

            <div className="table-container">
              {filteredCalls.length === 0 ? (
                <div className="latency-table-empty">
                  <Info className="empty-icon" />
                  <h4>No call records matched search criteria</h4>
                  <p>Try modifying your filters or typing a different customer name.</p>
                </div>
              ) : (
                <table id="latency-table-id" className="custom-table-container">
                  <thead className="table-head" style={{ position: "sticky", top: "0", zIndex: "1" }}>
                    <tr>
                      <th className="tbl-header-standard">S.No</th>
                      {headers.map((header) => (
                        <th key={header.value} className="tbl-header-standard">
                          {header.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCalls.map((call, index) => {
                      const isExpanded = expandedCallId === call.id;
                      const cellClass = `tbl-cell-standard ${isExpanded ? "cell-expanded-bg" : ""}`;
                      return (
                        <React.Fragment key={call.id}>
                          <tr
                            className={`latency-table-tr ${isExpanded ? "expanded" : ""}`}
                            onClick={() => toggleExpandRow(call.id)}
                          >
                            <td className={cellClass}>
                              {(currentPage - 1) * postsPerPage + index + 1}
                            </td>
                            {headers.map((header) => {
                              if (header.value === "id") {
                                return (
                                  <td key={header.value} className={`${cellClass} font-mono text-xs text-slate`}>
                                    {call.id}
                                  </td>
                                );
                              }
                              if (header.value === "customer") {
                                return (
                                  <td key={header.value} className={cellClass}>
                                    <div className="tbl-cust-info" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                      <span className="cust-name">{call.customer}</span>
                                      <span className="cust-phone">{call.phone}</span>
                                    </div>
                                  </td>
                                );
                              }
                              if (header.value === "campaign") {
                                return (
                                  <td key={header.value} className={cellClass}>
                                    <span className="tbl-campaign-badge">{call.campaign}</span>
                                  </td>
                                );
                              }
                              if (["stt", "llm", "tts"].includes(header.value)) {
                                return (
                                  <td key={header.value} className={cellClass}>
                                    <div className="engine-tbl-val" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                      <span className="engine-lbl">{call[header.value]}</span>
                                      <span className="engine-lat">{call[`${header.value}_lat`]}s</span>
                                    </div>
                                  </td>
                                );
                              }
                              if (header.value === "total") {
                                return (
                                  <td key={header.value} className={`${cellClass} font-semibold text-slate-dark`}>
                                    {call.total}s
                                  </td>
                                );
                              }
                              if (header.value === "status") {
                                return (
                                  <td key={header.value} className={cellClass}>
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                      <span className={`badge-status ${call.status === "Success" ? "success" : "warning"}`}>
                                        {call.status === "Success" ? (
                                          <CheckCircle className="badge-status-icon" />
                                        ) : (
                                          <AlertTriangle className="badge-status-icon" />
                                        )}
                                        {call.status}
                                      </span>
                                    </div>
                                  </td>
                                );
                              }
                              return (
                                <td key={header.value} className={cellClass}>
                                  {call[header.value] ?? "--"}
                                </td>
                              );
                            })}
                          </tr>

                          {isExpanded && (
                            <tr className="expanded-row-details">
                              <td colSpan={headers.length + 1}>
                                <div className="diagnostics-panel animate-slide-down">
                                  <h4 className="diagnostics-title">
                                    <Sparkles className="diagnostic-header-glow" /> Conversation Latency Breakdown Diagnostics
                                  </h4>
                                  <p className="diagnostics-subtitle">
                                    Precise segment durations measured sequentially for every conversational turn.
                                  </p>

                                  <div className="diagnostic-timeline">
                                    {getSequentialDetails(call).map((turn, tIdx) => (
                                      <div key={tIdx} className={`timeline-turn ${turn.role.toLowerCase()}`}>
                                        <div className="turn-avatar">
                                          {turn.role === "Customer" ? "C" : "AI"}
                                        </div>
                                        <div className="turn-bubble">
                                          <div className="bubble-header">
                                            <span className="bubble-role">{turn.role}</span>
                                            <span className="bubble-time">{call.time}</span>
                                          </div>
                                          <p className="bubble-text">{turn.text}</p>

                                          {(turn.stt > 0 || turn.llm > 0 || turn.tts > 0) && (
                                            <div className="turn-latency-metrics">
                                              {turn.stt > 0 && (
                                                <div className="turn-lat-p">
                                                  <span className="p-lbl">STT Transcription:</span>
                                                  <div className="p-bar-outer">
                                                    <div
                                                      className="p-bar-inner bg-stt"
                                                      style={{ width: `${Math.min((turn.stt / 1.5) * 100, 100)}%` }}
                                                    />
                                                    <span className="p-val">{turn.stt}s</span>
                                                  </div>
                                                </div>
                                              )}
                                              {turn.llm > 0 && (
                                                <div className="turn-lat-p">
                                                  <span className="p-lbl">LLM Synthesis:</span>
                                                  <div className="p-bar-outer">
                                                    <div
                                                      className="p-bar-inner bg-llm"
                                                      style={{ width: `${Math.min((turn.llm / 1.5) * 100, 100)}%` }}
                                                    />
                                                    <span className="p-val">{turn.llm}s</span>
                                                  </div>
                                                </div>
                                              )}
                                              {turn.tts > 0 && (
                                                <div className="turn-lat-p">
                                                  <span className="p-lbl">TTS Generation:</span>
                                                  <div className="p-bar-outer">
                                                    <div
                                                      className="p-bar-inner bg-tts"
                                                      style={{ width: `${Math.min((turn.tts / 1.5) * 100, 100)}%` }}
                                                    />
                                                    <span className="p-val">{turn.tts}s</span>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {filteredCalls.length > 0 && (
              <div>
                <Pagination
                  pagination={paginationObj}
                  postsPerPage={postsPerPage}
                  handlePageSizeChange={handlePageSizeChange}
                  fetchDetail={handlePageChange}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
